import type { UIRouterReact } from "@uirouter/react";

import { setCountryFilterData } from "app/action_types/country_filter_action-types";
import { swSettings } from "app/common/services/swSettings";
import { SwRedux } from "app/services/SwRedux";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import type { IRouterState } from "single-spa/react-app/router/IRouterState";

export const routerOnBefore = (router: UIRouterReact) => {
    const swRedux = SwRedux.getInstance();
    router.transitionService.onBefore({}, async (transition) => {
        if (transition.to().name !== transition.from().name && swRedux.getState().countryFilter.countriesData) {
            swRedux.dispatch(setCountryFilterData(null));
        }

        const toState = transition.to() as IRouterState;
        const toParams = { ...transition.params() };

        // Fallback for lazy-loaded future states: when the lazyLoadHook's retryTransition
        // fails to URL-match the newly registered child states, this hook runs with the
        // future state as the target. Try a URL match to redirect to the correct child state.
        if (toState.name?.endsWith(".**")) {
            const $url = router.urlService;
            const match = $url.match($url.parts());
            if (match?.rule && "state" in match.rule) {
                return router.stateService.target(
                    (match.rule as { state: string }).state,
                    match.match,
                    transition.options(),
                );
            }
            return false;
        }

        // Make sure we have the full component in swSettings.components
        // before we move forward
        if (toState && toState.configId) {
            const finalConfigId =
                typeof toState.configId === "function"
                    ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      (toState.configId as any)(toState, toParams)
                    : toState.configId;
            await swSettings.verifyOrFetchComponentById([finalConfigId]);
        }
        const toComponentName = proNavigator.getConfigIdFromStateConfig(toState, toParams);
        if (!toComponentName) {
            // cancel transition
            return false;
        }
        // set current component config to target config
        // if we have a dependancies array, add those as well
        const toComponentIds: string[] = [toComponentName];
        if ("configIdDeps" in toState) {
            toState.configIdDeps?.forEach((configId) => {
                toComponentIds.push(configId);
            });
        }
        await swSettings.setCurrent(toComponentIds);
    });
};
